import { amplifyRuntime, hasStoredSession } from '@/lib/amplify'

/**
 * The signed-in person, as the app sees them. Groups come from the signed ID token — the same
 * claim the admin mutations check — so "is an admin" here can never disagree with the server.
 */
export interface Session {
    userId: string
    email: string | null
    groups: string[]
}

/**
 * Read the current Cognito session, or null for a guest.
 *
 * `fetchAuthSession` RESOLVES with no tokens rather than throwing when a refresh fails (the
 * thirty-days-idle case). Treating that as signed in would hand the app an identity with no email
 * and no groups, so no id token means guest.
 */
export async function readSession(): Promise<Session | null> {
    // No tokens on the device means a guest, known without downloading the SDK: the common case
    // on a marketing page, and the reason the header can stop showing a skeleton at once.
    if (!hasStoredSession()) { return null }
    try {
        const { fetchAuthSession, getCurrentUser } = await amplifyRuntime()
        const { userId } = await getCurrentUser()
        const { tokens } = await fetchAuthSession()
        const payload = tokens?.idToken?.payload
        if (!payload) { return null }
        const rawGroups = payload['cognito:groups']
        const groups = Array.isArray(rawGroups) ? rawGroups.map(String) : []
        const email = typeof payload['email'] === 'string' ? payload['email'] : null
        return { userId, email, groups }
    } catch {
        return null
    }
}

/** The Cognito group an administrator is in. The admin app's guard and any admin-only schema rule read the same name. */
export const ADMIN_GROUP = 'admins'

export async function endSession(): Promise<void> {
    const { signOut } = await amplifyRuntime()
    await signOut()
}
